import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { motion } from 'framer-motion'; // Import motion

import Spinner from './Spinner';
import * as api from '../services/api'; // Import all API functions
import { DownloadIcon } from '@heroicons/react/outline'; // Example icon import

import { ResultContext } from '../contexts/ResultContext';

// Helper function to generate unique model ID for this session
const generateModelId = (baseName = "recency") => {
  const now = new Date();
  const timestamp = `${now.getFullYear().toString().slice(-2)}${(now.getMonth() + 1).toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}`;
  // Use a more unique identifier for production use
  return `${baseName}_${timestamp}`;
};

// Helper function to generate user ID string
const generateUserId = (name = "") => {
  return name.toLowerCase().replace(/\s+/g, '');
};

// Component to display results safely
export const ResultDisplay = ({ title, data }) => {
  if (!data) return null;

  let content;
  if (typeof data === 'string' && data.startsWith('<')) {
    // If it looks like HTML, render it dangerously
    content = <div className="prose prose-sm max-w-none dark:prose-invert" dangerouslySetInnerHTML={{ __html: data }} />;
  } else if (typeof data === 'object') {
    // If it's an object, stringify it (nicely formatted)
    content = <pre className="text-xs bg-gray-100 dark:bg-gray-700 p-2 rounded overflow-x-auto">{JSON.stringify(data, null, 2)}</pre>;
  } else {
    // Otherwise, display as is
    content = <p className="text-sm text-gray-700 dark:text-gray-300">{data}</p>;
  }

  return (
    <div className="mt-4 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50">
      <h4 className="font-semibold text-md mb-2 text-gray-800 dark:text-white">{title}</h4>
      {content}
    </div>
  );
};

// New Component for displaying metrics nicely
const MetricDisplay = ({ label, value, isPercentage = false }) => {
  let displayValue = value;

  if (typeof value === 'number') {
    if (isPercentage) {
      displayValue = `${(value * 100).toFixed(1)}%`;
    } else if (Number.isInteger(value)) {
      // If it's an integer, display as is
      displayValue = value;
    } else {
      // If it's a float, show 2 decimal places
      displayValue = value.toFixed(2);
    }
  } else if (value === null || value === undefined) {
    displayValue = 'N/A'; // Handle null/undefined
  }
  // Keep strings as they are (e.g., 'N/A')

  return (
    <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
      <span className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{label}</span>
      <span className="text-lg font-semibold text-gray-900 dark:text-white">
        {displayValue}
      </span>
    </div>
  );
};

function CLTVInteraction({
  model,
  extraData,
  onTrainingComplete,
  onUploadComplete,
  onPredictionComplete // <-- Add new prop
}) {
  const { user } = useAuth0();
  const [currentStep, setCurrentStep] = useState('initial'); // initial, uploading, validating, training, polling_train, metrics, uploading_test, validating_test, predicting, polling_predict, results
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [progress, setProgress] = useState(0); // For polling progress
  const [uploadProgress, setUploadProgress] = useState(0); // For upload progress

  // Training Flow State
  const [trainFile, setTrainFile] = useState(null);
  const [trainUploadResult, setTrainUploadResult] = useState(null);
  const [trainValidationResult, setTrainValidationResult] = useState(null);
  const [trainTask, setTrainTask] = useState(null);
  const [cltvData, setCLTVData] = useState(null);
  // State to hold the final raw result from the status endpoint upon completion
  const [finalTaskResult, setFinalTaskResult] = useState(null);

  // Testing/Prediction Flow State
  const [testFile, setTestFile] = useState(null);
  const [testUploadResult, setTestUploadResult] = useState(null);
  const [testValidationResult, setTestValidationResult] = useState(null);
  const [predictTask, setPredictTask] = useState(null); // Assuming predict might also be async
  const [predictResult, setPredictResult] = useState(null); // Keep this for predict-specific results? Or merge? For now, keep separate.

  // Generate IDs once per component instance
  const modelIdRef = useRef(generateModelId(model.name?.toLowerCase().replace(/\s+/g, '_')));
  const userIdRef = useRef(generateUserId(user?.name || ''));

  // Polling ref
  const pollingIntervalRef = useRef(null);

  // Clear polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // Common button class for primary actions
  const primaryButtonClass = "px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#0062cc] hover:bg-[#0052ac] disabled:opacity-50 disabled:cursor-not-allowed";
  // Common button class for secondary actions
  const secondaryButtonClass = "px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed";


  // --- API Call Handlers ---

  const handleApiCall = async (apiFunc, params, successStep, resultSetter, progressCallback) => {
    setIsLoading(true);
    setError(null);
    setStatusMessage('Processing...');
    setProgress(0); // Reset polling progress
    setUploadProgress(0); // Reset upload progress
    try {
      // Pass progress callback if provided (only for upload)
      const result = await apiFunc(params, progressCallback);
      if (resultSetter) resultSetter(result);
      setCurrentStep(successStep);
      setUploadProgress(100); // Mark upload as complete on success
      return result;
    } catch (err) {
      console.error(`Error during ${successStep}:`, err);
      const errorMessage = err.response?.data?.detail || err.message || 'Unknown error';
      setError(`Operation failed: ${errorMessage}`);
      setStatusMessage(`Operation failed.`);
      setCurrentStep('error');
      return null;
    } finally {
      setIsLoading(false);
      // Clear status message only if no error occurred
      if (!error) {
        setStatusMessage('');
      }
    }
  };

  const handleUpload = async (dataType) => {
    const file = dataType === 'training' ? trainFile : testFile;
    if (!file || !user?.email) {
      setError(`Please select a ${dataType} file and ensure you are logged in.`);
      return;
    }

    setIsLoading(true);
    setError(null);
    setUploadProgress(0);
    setStatusMessage(`Uploading ${dataType} data...`);

    // Create FormData exactly as shown in the swagger UI example
    const formData = new FormData();
    formData.append('file', file); // Use the original CSV file
    formData.append('email_id', user.email);
    formData.append('model_id', modelIdRef.current);


    const resultSetter = dataType === 'training' ? setTrainUploadResult : setTestUploadResult;
    const nextStep = dataType === 'training' ? 'validating' : 'validating_test';

    // Define the progress callback for Axios
    const onUploadProgress = (progressEvent) => {
      if (progressEvent.total) {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(percentCompleted);
      } else {
        // Handle indeterminate progress if total size is not available
        setUploadProgress(50); // Example: set to 50% or use a different visual indicator
      }
    };

    // Call the generic handler, passing the progress callback
    const uploadResult = await handleApiCall(api.uploadCLTVData, formData, nextStep, resultSetter, onUploadProgress);

    // If upload was successful and callback exists, call it
    if (uploadResult && onUploadComplete) {
      onUploadComplete(uploadResult);
    }
  };

  const { setResult } = useContext(ResultContext);

  const handleValidate = async (dataType) => {
    const uploadResult = dataType === 'training' ? trainUploadResult : testUploadResult;
    if (!user?.email) {
      setError(`Cannot validate: Missing upload details or user email.`);
      return;
    }




    const params = {
      file_key: uploadResult.details.s3_key,
      email_id: user.email,
      user_id: user.email,
      model_id: modelIdRef.current,

    };

    setStatusMessage(`Validating ${dataType} data...`);

    const resultSetter = dataType === 'training' ? setTrainValidationResult : setTestValidationResult;
    const nextStep = dataType === 'training' ? 'training' : 'predicting';

    handleApiCall(api.validateCLTVData, params, nextStep, resultSetter);
  };

  const handleSampleUpload = async (dataType) => {
    setIsLoading(true);
    setError(null);
    setStatusMessage('Processing...');

    const uploadResult = dataType === 'training' ? trainUploadResult : testUploadResult;
    if (!user?.email) {
      setError(`Cannot validate: Missing upload details or user email.`);
      return;
    }

    const path = extraData?.training_url.split("/").slice(3).join("/").split("?")[0];

    console.log(path); // Output: sample-files/OnlineRetail_train.csv

    const params = {
      sample_s3_key: path,
      email_id: user.email,
      user_id: user.email,
      model_id: modelIdRef.current,
      data_type: dataType,
    };

    const nextStep = 'validating';

    const statusResult = await api.useSampleDataApi('cltv', params);
    setTrainUploadResult(statusResult)
    setCurrentStep(nextStep);

    if (statusResult) {
      onUploadComplete(statusResult);
      setStatusMessage(`Ready for next`);
      setIsLoading(false)
    }
  };

  const handleTrain = async () => {
    if (!trainValidationResult || !user?.email) {
      setError(`Cannot train: Missing validation details or user email.`);
      return;
    }



    setStatusMessage('Starting model training...');
    setFinalTaskResult(null); // Reset final result when starting training

    const result = await handleApiCall(api.cltvTaskStatusapi, trainValidationResult.task_manager_id, 'polling_train', setTrainTask);

    if (result?.task_manager_details.status == 'completed') {
      setResult(result)
      setCLTVData(result)
      setCurrentStep('complete');
    }
    else {
      // Changed successStep to 'results' to directly show the final raw result
      startPolling(result.task_manager_details.task_id, 'complete');
    }
  };

  const startPolling = (taskId, successStep) => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    setStatusMessage(`Task started. Monitoring progress...`);
    setProgress(0);
    setFinalTaskResult(null); // Ensure final result is clear at start of polling

    pollingIntervalRef.current = setInterval(async () => {
      try {
        const statusResult = await api.cltvTaskStatusapi(taskId);
        setCLTVData(statusResult)
        setResult(statusResult)
        if (!statusResult) {
          console.warn("Polling received incomplete status result:", statusResult);
          // Optional: Keep polling or show an intermediate error?
          // setStatusMessage("Waiting for task status...");
          return; // Skip update if data is missing
        }

        const taskStatus = statusResult.task_manager_details;
        const metadata = taskStatus.metadata || {};
        const currentStage = metadata.stage;
        const currentApiStatus = taskStatus.status;
        console.log('sssss', currentApiStatus);

        if (currentStage == 'completed' || currentApiStatus == 'completed') {
          // --- Task Completed ---
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
          setFinalTaskResult(statusResult); // Store the full final result
          setProgress(100); // Set progress to 100%
          setStatusMessage('Task completed successfully.');
          setCurrentStep(successStep); // Go to the results display step

          // Call the callback prop if it exists
          if (onTrainingComplete) {
            onTrainingComplete(statusResult);
          }

          handlePredict(taskStatus.task_id)

        } else if (currentApiStatus === 'failed') {
          // --- Task Failed ---
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
          const pollError = metadata.error || taskStatus.error || 'Unknown reason'; // Get error details
          setError(`Task failed: ${pollError}`);
          setStatusMessage('Task failed.');
          setCurrentStep('error');

        } else {
          // --- Task Still Processing ---
          const numericalProgress = metadata.progress || 0;
          const displayProgress = Math.min(numericalProgress, 75); // Cap progress at 75%
          setProgress(numericalProgress);
          // Use stage if available, otherwise fallback status
          const displayStage = currentStage || currentApiStatus || 'Processing';
          setStatusMessage(`Task Status: ${displayStage} (${numericalProgress}%)`);
        }
      } catch (err) {
        console.error('Polling error:', err);
        setError(`Failed to get task status: ${err.message || 'Polling request failed'}`);
        // Decide whether to stop polling on error
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
        setCurrentStep('error'); // Go to error state on polling failure
      }
    }, 5000); // Poll every 5 seconds
  };

  const handlePredict = async (tid) => {

    const predictResultData = await api.cltvResultModelApi(tid, 'full_results');  // json csv plot_list plot_data
    setResult(predictResultData)

    if (predictResultData && onPredictionComplete) {
      onPredictionComplete(predictResultData);
    }

  };

  const handleSampleFileDownload = (fileUrl) => {

    //const fileName = 'downloaded-file.pdf';

    try {

      setIsLoading(true)

      const link = document.createElement('a');
      link.href = fileUrl;
      // link.setAttribute('download', fileName); // optional, suggest filename
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => {
        setIsLoading(false)
        setStatusMessage('Check Download folder');
      }, 3000);

    } catch (error) {
      console.log('errr', error);

    }

  };

  const handleDownloadCsv = async () => {

    setStatusMessage('Downloading...');

    setIsLoading(true)
    const csvResultData = await api.cltvResultModelApi(finalTaskResult?.task_manager_details?.task_id, 'csv');  // json csv plot_list plot_data
    handleSampleFileDownload(csvResultData.download_url)

  }


  // --- Render Logic ---

  const renderStepUI = () => {
    // Helper function to wrap step content in consistent styling
    const stepContent = (content, title) => (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50"
      >
        <h4 className="text-lg font-semibold text-gray-800 dark:text-white mb-3">{title}</h4>
        <div className="space-y-4">
          {content}
        </div>
      </motion.div>
    );

    switch (currentStep) {
      case 'initial':
      case 'uploading': // Allow re-upload
        return stepContent(
          <>
            <button
              onClick={() => handleSampleUpload('training')}
              disabled={isLoading}
              className={primaryButtonClass}
            >
              {isLoading ? <><Spinner size="xs" className="mr-2" /> Uploading...</> : 'Use Sample Data'}
            </button>
            <p>OR</p>
            <span
              className="underline text-blue-600 cursor-pointer hover:text-blue-800 text-sm"
              onClick={() => handleSampleFileDownload(extraData?.training_url)}
            >
              Download sample training file
            </span>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Select your training data file (.csv expected).</p>
            <input
              type="file"
              accept=".csv"
              onChange={(e) => setTrainFile(e.target.files[0])}
              className="block w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-[#0062cc]/10 dark:file:bg-[#0062cc]/20 file:text-[#0062cc] dark:file:text-blue-300 hover:file:bg-[#0062cc]/20 dark:hover:file:bg-[#0062cc]/30 mb-2"
            />
            {/* Upload Progress Bar */}
            {isLoading && uploadProgress > 0 && (
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 my-1">
                <div className="bg-[#0062cc] h-2.5 rounded-full" style={{ width: `${uploadProgress}%` }}></div>
              </div>
            )}
            <button
              onClick={() => handleUpload('training')}
              disabled={!trainFile || isLoading}
              className={primaryButtonClass}
            >
              {isLoading ? <><Spinner size="xs" className="mr-2" /> Uploading...</> : 'Upload Training Data'}
            </button>
          </>,
          "1. Upload Training Data"
        );
      case 'validating':
        const trainRows = trainUploadResult?.file_details?.rows || 'N/A';
        const trainCols = trainUploadResult?.file_details?.columns || 'N/A';
        return stepContent(
          <>
            {/* Show Rows and Columns instead of full JSON 
            <div className="grid grid-cols-2 gap-4 mb-4">
              <MetricDisplay label="Rows Processed" value={trainRows} />
              <MetricDisplay label="Columns Found" value={trainCols} />
            </div>*/}
            {/* Display original filename for context */}
            {trainUploadResult?.file_details?.original_filename && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                File: {trainUploadResult.file_details.original_filename}
              </p>
            )}
            <button
              onClick={() => handleValidate('training')}
              disabled={isLoading}
              className={primaryButtonClass}
            >
              {isLoading ? <><Spinner size="xs" className="mr-2" /> Validating...</> : 'Validate Training Data'}
            </button>
          </>,
          "2. Validate Training Data"
        );
      case 'training':
        const trainValResult = trainValidationResult;
        const trainValCols = trainValResult?.details?.data_shape?.columns || 'N/A';
        const trainMapping = trainValResult?.details?.mapping;
        const trainMappingValues = trainMapping ? Object.values(trainMapping) : [];

        return stepContent(
          <>
            {/* Show specific validation results 
            <MetricDisplay label="Columns Validated" value={trainValCols} />
            */}
            {trainMappingValues.length > 0 && (
              <div className="mt-4">
                <h5 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-2">Input Columns Used:</h5>
                <ul className="list-disc list-inside space-y-1 pl-2">
                  {trainMappingValues.map((colName, index) => (
                    <li key={index} className="text-sm text-gray-600 dark:text-gray-400">
                      {colName}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Display validation message */}
            {trainValResult?.message && (
              <p className="text-sm text-green-600 dark:text-green-400 mt-4 bg-green-50 dark:bg-green-900/30 p-2 rounded">
                {trainValResult.message}
              </p>
            )}

            <button
              onClick={handleTrain}
              disabled={isLoading}
              className={`${primaryButtonClass} mt-4`}
            >
              {isLoading ? <><Spinner size="xs" className="mr-2" /> Starting...</> : 'Start Training'}
            </button>
          </>,
          "3. Train Model"
        );
      case 'polling_train':
        return stepContent(
          <>
            <h4 className="text-md font-semibold text-gray-800 dark:text-white">Training in Progress...</h4>
            {/* <p className="text-sm text-gray-600 dark:text-gray-400">Task ID: {trainTask?.task_id}</p> */}
            {/* Polling Progress Bar - capped at 75% visually during polling */}
            <div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 my-1">
                <div className="bg-[#0062cc] h-2.5 rounded-full transition-width duration-500 ease-out" style={{ width: `${progress}%` }}></div>
              </div>
              {/* Show 75% max until truly complete */}
              <p className="text-xs text-center text-gray-600 dark:text-gray-400">{progress === 100 ? '100%' : `${progress}%`} Complete</p>
            </div>
            {/* No button needed here now, automatically proceeds on completion */}
          </>,
          "Training Status" // Simplified title
        );
      // Removed 'metrics' step - now handled by 'results' step after polling
      case 'uploading_test':
        return stepContent(
          <>
            <h4 className="text-md font-semibold text-gray-800 dark:text-white">4. Upload Testing Data</h4>
            <p className="text-sm text-gray-600 dark:text-gray-300">Select your testing data file (.csv expected).</p>
            <input
              type="file"
              accept=".csv"
              onChange={(e) => { setTestFile(e.target.files[0]); setUploadProgress(0); setError(null); }}
              className="block w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border file:border-gray-300 dark:file:border-gray-600 file:text-sm file:font-semibold file:bg-gray-100 dark:file:bg-gray-700 file:text-gray-700 dark:file:text-gray-300 hover:file:bg-gray-200 dark:hover:file:bg-gray-600"
            />
            {/* Upload Progress Bar */}
            {isLoading && currentStep === 'uploading_test' && uploadProgress > 0 && (
              <div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 my-1">
                  <div className="bg-green-600 h-2.5 rounded-full" style={{ width: `${uploadProgress}%` }}></div>
                </div>
                <p className="text-xs text-center text-gray-600 dark:text-gray-400">Uploading: {uploadProgress}%</p>
              </div>
            )}
            <button
              onClick={() => handleUpload('testing')}
              disabled={!testFile || isLoading}
              className={primaryButtonClass}
            >
              {isLoading ? <><Spinner size="xs" className="mr-2" /> Uploading...</> : 'Upload Testing Data'}
            </button>
          </>,
          "4. Upload Testing Data"
        );
      case 'validating_test':
        const testRows = testUploadResult?.file_details?.rows || 'N/A';
        const testCols = testUploadResult?.file_details?.columns || 'N/A';
        return stepContent(
          <>
            {/* Show Rows and Columns instead of full JSON */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <MetricDisplay label="Rows Processed" value={testRows} />
              <MetricDisplay label="Columns Found" value={testCols} />
            </div>
            {/* Display original filename for context */}
            {testUploadResult?.file_details?.original_filename && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                File: {testUploadResult.file_details.original_filename}
              </p>
            )}
            <button
              onClick={() => handleValidate('testing')}
              disabled={isLoading}
              className={primaryButtonClass}
            >
              {isLoading ? <><Spinner size="xs" className="mr-2" /> Validating...</> : 'Validate Testing Data'}
            </button>
          </>,
          "5. Validate Testing Data"
        );
      case 'predicting':
        const testValResult = testValidationResult;
        const testValCols = testValResult?.details?.data_shape?.columns || 'N/A';
        const testMapping = testValResult?.details?.mapping;
        const testMappingValues = testMapping ? Object.values(testMapping) : [];

        return stepContent(
          <>
            {/* Show specific validation results */}
            <MetricDisplay label="Columns Validated" value={testValCols} />

            {testMappingValues.length > 0 && (
              <div className="mt-4">
                <h5 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-2">Input Columns Used:</h5>
                <ul className="list-disc list-inside space-y-1 pl-2">
                  {testMappingValues.map((colName, index) => (
                    <li key={index} className="text-sm text-gray-600 dark:text-gray-400">
                      {colName}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Display validation message */}
            {testValResult?.message && (
              <p className="text-sm text-green-600 dark:text-green-400 mt-4 bg-green-50 dark:bg-green-900/30 p-2 rounded">
                {testValResult.message}
              </p>
            )}

            <button
              onClick={handlePredict}
              disabled={isLoading}
              className={`${primaryButtonClass} mt-4`}
            >
              {isLoading ? <><Spinner size="xs" className="mr-2" /> Predicting...</> : 'Run Prediction'}
            </button>
          </>,
          "6. Predict"
        );
      case 'results': // Combined results step
        if (predictResult) {
          // --- Display Prediction Results --- 
          const stats = predictResult.segment_stats;
          const metrics = predictResult.metrics?.test_metrics;
          const liftMetrics = predictResult.lift_metrics;
          const preview = predictResult.preview_data;

          // --- Parse lift data --- 
          let parsedLiftData = [];
          if (liftMetrics?.lift_values) {
            try {
              const rawLiftData = JSON.parse(liftMetrics.lift_values);

              if (rawLiftData.index && rawLiftData['Lift (Total Sales)'] && rawLiftData['Cumulative Lift']) {
                parsedLiftData = rawLiftData.index.map((idx, i) => ({
                  index: idx,
                  'Lift (Total Sales)': rawLiftData['Lift (Total Sales)'][i] || 0,
                  'Cumulative Lift': rawLiftData['Cumulative Lift'][i] || 0,
                }));
              }
            } catch (parseError) {
              console.error("Error parsing lift_values JSON:", parseError);
            }
          }
          // --- End Parse lift data --- 

          return stepContent(
            <>
              {/* Summary Stats */}
              {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <MetricDisplay label="Total Customers" value={stats.total_customers || 'N/A'} />
                  <MetricDisplay label="High-Value Customers" value={stats.high_value_count || 'N/A'} />
                  <MetricDisplay label="Regular Customers" value={stats.regular_count || 'N/A'} />
                  <MetricDisplay label="High-Value Ratio" value={stats.high_value_ratio || 0} isPercentage={true} />
                </div>
              )}

              {/* Prediction/Test Metrics */}
              {metrics && (
                <div className="mb-6">
                  <h5 className="text-md font-semibold text-gray-800 dark:text-white mb-3">Prediction Performance Metrics</h5>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <MetricDisplay label="Accuracy" value={metrics.accuracy || 0} isPercentage={true} />
                    <MetricDisplay label="Precision" value={metrics.precision || 0} isPercentage={false} />
                    <MetricDisplay label="Recall" value={metrics.recall || 0} isPercentage={false} />
                    <MetricDisplay label="F1 Score" value={metrics.f1 || 0} isPercentage={false} />
                  </div>
                </div>
              )}



              {/* Start Over Button */}
              <div className="mt-6">
                <button
                  onClick={() => {
                    setCurrentStep('initial');
                    setError(null);
                    setStatusMessage('');
                    setTrainFile(null);
                    setTestFile(null);
                    setTrainUploadResult(null);
                    setTestUploadResult(null);
                    setTrainValidationResult(null);
                    setTestValidationResult(null);
                    setTrainTask(null);
                    setPredictTask(null);
                    setFinalTaskResult(null);
                    setPredictResult(null); // Also clear predict result
                    setProgress(0);
                    setUploadProgress(0);
                  }}
                  className={secondaryButtonClass}
                >
                  Start Over
                </button>
              </div>
            </>,
            "Prediction Results"
          );
        } else if (finalTaskResult) {
          // --- Display Training Results (Simplified) ---
          const resultTitle = "Training Task Completed";

          return stepContent(
            <>
              {/* Simple Confirmation Message */}
              <p className="text-lg text-center text-green-600 dark:text-green-400 font-semibold py-4">
                Training Task Completed Successfully!
              </p>

              {/* Action Buttons */}
              <div className="mt-6 flex justify-center space-x-4"> {/* Centered buttons */}
                <button
                  onClick={() => setCurrentStep('uploading_test')}
                  className={primaryButtonClass}
                >
                  Proceed to Testing
                </button>
                <button
                  onClick={() => {
                    setCurrentStep('initial');
                    setError(null);
                    setStatusMessage('');
                    setTrainFile(null);
                    setTestFile(null);
                    setTrainUploadResult(null);
                    setTestUploadResult(null);
                    setTrainValidationResult(null);
                    setTestValidationResult(null);
                    setTrainTask(null);
                    setPredictTask(null);
                    setFinalTaskResult(null);
                    setPredictResult(null);
                    setProgress(0);
                    setUploadProgress(0);
                  }}
                  className={secondaryButtonClass}
                >
                  Start Over
                </button>
              </div>
            </>,
            resultTitle
          );
        } else {
          // Should not happen if logic is correct, but provide a fallback
          return stepContent(<p>Waiting for results...</p>, "Results");
        }
      case 'error':
        return stepContent(
          <>
            <p className="text-red-600 dark:text-red-400">An error occurred. Please check the message below and try again.</p>
            <button
              onClick={() => {
                setError(null);
                setStatusMessage('');
                // Reset relevant states to allow retry from initial step
                setCurrentStep('initial');
                setTrainFile(null);
                setTestFile(null);
                // ... maybe reset other states depending on where the error occurred
              }}
              className={primaryButtonClass}
            >
              Try Again
            </button>
          </>,
          "Operation Failed"
        );
      case 'complete':
        return stepContent(
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">

              <MetricDisplay label="No. of customers processed" value={cltvData?.task_manager_details?.metadata?.metrics?.num_customers_processed || 'N/A'} />
              <MetricDisplay label="Avg. predicted CLTV" value={cltvData?.task_manager_details?.metadata?.metrics?.avg_predicted_cltv || 'N/A'} />
              <MetricDisplay label="CLTV prediction period (months)" value={cltvData?.task_manager_details?.metadata?.metrics?.cltv_prediction_period_months || 'N/A'} />
              <MetricDisplay label="Analysis window" value={cltvData?.task_manager_details?.metadata?.metrics?.analysis_window_start_date + ' to ' + cltvData?.task_manager_details?.metadata?.metrics?.analysis_window_end_date} />
            </div>

            {/* <div class="flex items-center justify-between  border-b"> */}
            <div class="flex items-center justify-center  border-b">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Output Result CLTV file  </h3>

              <button
                onClick={handleDownloadCsv}
                disabled={isLoading}
                className={`${primaryButtonClass} mb-4 ml-3`}
              >
                {isLoading ? <><Spinner size="xs" className="mr-2" /> Downloading...</> : 'Download csv file'}
              </button>
            </div>
          </>,
          "Completed Segmentation. Check visualization tab"
        );
      default:
        return <p>Unknown step: {currentStep}</p>;
    }
  };

  return (
    <div className="space-y-6 p-4 border rounded-lg bg-white dark:bg-gray-900 shadow-sm">
      {/* <h3 className="text-xl font-semibold border-b border-gray-200 dark:border-gray-700 pb-2 text-gray-900 dark:text-white">Recency Frequency Monetory Model Workflow</h3> */}

      {/* Status Area */}
      <div className="p-3 rounded-lg bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 min-h-[60px] flex items-center">
        {isLoading && !statusMessage.toLowerCase().includes("uploading") && ( // Don't show generic spinner during upload progress
          <div className="flex items-center space-x-2 text-[#0062cc] dark:text-blue-400">
            <Spinner size="sm" />
            <p>{statusMessage || 'Processing...'}</p>
          </div>
        )}
        {error && <div className="text-red-600 dark:text-red-400 font-medium">Error: {error}</div>}
        {!isLoading && statusMessage && !error && <div className="text-gray-700 dark:text-gray-300">{statusMessage}</div>}
        {!isLoading && !error && !statusMessage && <div className="text-gray-500 dark:text-gray-400 italic">Ready for next step.</div>}
      </div>

      {/* Dynamic Step UI */}
      <div className="mt-4">
        {renderStepUI()}
      </div>

      {/* Display User Info */}
      {user?.email && <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-4">Logged in as: {user.email}</p>}
    </div>
  );
}

export default CLTVInteraction;
